
// Libaries
import { Component, Vue, namespace } from "nuxt-property-decorator";

// Components

import { replaceToUmlauts, returnPriceFormatDE } from "~/mixins/formatting";
import { decodePlaceName } from "~/operations/shared";

// Mixins
import { Jsonld } from "nuxt-jsonld";
import BreadCrumb from "~/components/molecules/display/BreadCrumb.vue";
import DistrictOtherLocations from "~/components/molecules/display/DistrictOtherLocations.vue";

import DistrictLocationsList from "~/components/molecules/display/DistrictLocationsList.vue";
import PelletsChartComparison from "~/components/organisms/chart/PelletsChartComparison.vue";
import DualVerticalDataTile from "~/components/organisms/display/geographySites/DualVerticalDataTile.vue";
import PelletsDataDisplay from "~/components/organisms/display/geographySites/PelletsDataDisplay.vue";
import { createBreadCrumbsStructuredData } from "~/mixins/breadcrumb";
import {
  translateStateName,
  translateStateToNutsCode,
} from "~/mixins/translations";
import { generateMetaTags } from "~/operations/meta-helper-functions";
import { STANDARD_PELLETS_QUANTITY } from "~/types";
import { CountyLink } from "~/types/Location";
import { PartnerType } from "~/types/Partner";
import { isCityState } from "~/utils/geographyUtils";
import { calculatePelletsChartMetrics } from "~/utils/priceUtils";

const FederalStatesModule = namespace("federalstates");
const BreadCrumbModule = namespace("breadcrumb");

@Jsonld
@Component({
  scrollToTop: true,
  components: {
    BreadCrumb,
    DistrictLocationsList,
    DistrictOtherLocations,
    PelletsDataDisplay,
    DualVerticalDataTile,
    PelletsChartComparison,
  },
  methods: {
    translateStateToNutsCode,
    translateStateName,
  },
})
export default class CountyPricesView extends Vue {
  head() {
    return {
      ...generateMetaTags(
        this.$route.path,
        this.isDistrictCity ? "district-city-pellets" : "landkreis-pellets",
        [{ k: "name", v: this.countyName }],
      ),
      link: [
        {
          rel: "canonical",
          href: `/pelletspreise/bundeslaender/${this.$route.params.category.toLowerCase()}/${this.districtName.toLowerCase()}`,
        },
      ],
      script: [
        {
          json: {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: createBreadCrumbsStructuredData(this.breadCrumb),
          },
          type: "application/ld+json",
        },
      ],
    };
  }
  translateStateName = translateStateName;
  translateStateToNutsCode = translateStateToNutsCode;

  STANDARD_PELLETS_QUANTITY = STANDARD_PELLETS_QUANTITY;

  @BreadCrumbModule.Mutation("setBreadCrumbForBundeslandAndCounty")
  setBreadCrumbForBundeslandAndCounty!: any;

  @BreadCrumbModule.State("breadCrumb")
  breadCrumb!: any;

  @FederalStatesModule.State("districtCityZipcodes")
  districtCityZipcodes;

  @FederalStatesModule.State("countyLinks")
  countyLinks!: CountyLink[];

  @FederalStatesModule.State("countyName")
  countyName!: string;

  @FederalStatesModule.State("communityLinks")
  communityLinks!: any[];

  @FederalStatesModule.State("pelletsCountyAssetsPrice")
  pelletsCountyAssetsPrice;

  @FederalStatesModule.State("pelletsStateAssetsPrice")
  pelletsStateAssetsPrice;

  @FederalStatesModule.State("districtCities")
  districtCities!: any[];

  @FederalStatesModule.State("pelletsChartData")
  pelletsChartData!: any;

  @FederalStatesModule.State("countyNutsCode")
  countyNutsCode!: string;

  readonly CITY_STATES = ["Bremen", "Berlin", "Hamburg"];

  async fetch() {
    try {
      await this.fetchInitialData();
      await Promise.all([this.fetchLocationData(), this.fetchPriceData()]);
      this.updateBreadcrumbs();
    } catch (error) {
      this.$nuxt.error({
        statusCode: 404,
        message: "Diese Ortschaft konnte nicht gefunden werden",
      });
    }
  }

  beforeDestroy(): void {
    this.$store.commit("federalstates/resetCountyInfo");
  }

  async updateBreadcrumbs(): Promise<void> {
    const { formatBreadCrumbByCountyAndBundesland } = await import(
      "~/mixins/breadcrumb"
    );
    const crumbs = formatBreadCrumbByCountyAndBundesland(
      this.$route.fullPath,
      this.$router,
      this.countyName,
    );

    this.setBreadCrumbForBundeslandAndCounty({
      breadCrumbData: crumbs,
      fullPath: this.$route.fullPath,
    });
  }

  async fetchPriceData(): Promise<void> {
    const stateNutsCode = translateStateToNutsCode(
      replaceToUmlauts(this.$route.params.category),
    );
    if (this.isCityStateValue) {
      await this.fetchCurrentPelletsStatePrice(stateNutsCode);
      await this.fetchPelletsStateChartData(stateNutsCode);
    } else {
      await Promise.all([
        this.fetchStateCountyDataPellets(stateNutsCode, this.countyNutsCode),
        this.fetchPelletsCountyDistrictChartData(this.countyNutsCode),
      ]);
    }
  }

  async fetchInitialData(): Promise<void> {
    try {
      this.redirectToWithoutUmlauts();
      await this.fetchCountyIdentData(
        translateStateName(this.$route.params.category),
        translateStateName(this.districtName),
        "PARTNER_PELLET",
      );
    } catch (error) {
      this.handleError(error);
    }
  }

  async fetchLocationData(): Promise<void> {
    if (this.isDistrictCity) {
      await Promise.all([
        this.fetchDistrictCityZipcodes(
          translateStateName(this.districtName),
          "PARTNER_PELLET",
        ),
        this.fetchOtherDistrictCities(
          translateStateToNutsCode(
            replaceToUmlauts(this.$route.params.category),
          ),
          "PARTNER_PELLET",
        ),
      ]);
    } else {
      const stateNutsCode = this.translateStateToNutsCode(
        replaceToUmlauts(this.$route.params.category),
      );
      await this.fetchStateAndCommunityData(stateNutsCode);
    }
  }

  async fetchDistrictCityZipcodes(
    districtCity: string,
    partnerType?: PartnerType,
  ): Promise<void> {
    await this.$store.dispatch("federalstates/fetchDistrictCityZipcodes", {
      districtCity,
      urlPrefix: "pelletspreise",
      partnerType,
    });
  }

  async fetchStateAndCommunityData(stateNutsCode: string): Promise<void> {
    await Promise.all([
      this.fetchCounties(stateNutsCode, "PARTNER_PELLET"),
      this.fetchCommunities(this.countyNutsCode, "PARTNER_PELLET"),
    ]);
  }

  async fetchCurrentPelletsStatePrice(stateNutsCode: string): Promise<void> {
    await this.$store.dispatch("federalstates/fetchCurrentPelletsStatePrice", {
      nutsCode: stateNutsCode,
    });
  }

  async fetchCountyIdentData(
    stateName: string,
    countyName: string,
    partnerType?: PartnerType,
  ): Promise<void> {
    await this.$store.dispatch("federalstates/fetchCountyIdentData", {
      stateName,
      countyName,
      partnerType,
    });
  }

  async fetchPelletsCountyDistrictChartData(countyNuts: string): Promise<void> {
    await this.$store.dispatch(
      "federalstates/fetchPelletsCountyDistrictChartData",
      {
        countyNutsCode: countyNuts,
      },
    );
  }

  async fetchOtherDistrictCities(
    stateNutsCode: string,
    partnerType?: PartnerType,
  ): Promise<void> {
    await this.$store.dispatch("federalstates/fetchDistrictCities", {
      stateNutsCode,
      shouldFilter: true,
      includeLinkField: true,
      currentDistrictCity: replaceToUmlauts(this.countyName),
      partnerType,
    });
  }

  async fetchCommunities(
    communityNutsCode: string,
    partnerType?: PartnerType,
  ): Promise<void> {
    await this.$store.dispatch("federalstates/fetchCommunityLinks", {
      communityNutsCode,
      partnerType,
    });
  }

  async fetchCounties(
    statesNutsCode: string,
    partnerType?: PartnerType,
  ): Promise<void> {
    await this.$store.dispatch("federalstates/fetchCountyLinks", {
      statesNutsCode,
      partnerType,
    });
  }
  async fetchStateCountyDataPellets(
    stateNutsCode: string,
    countyNuts: string,
  ): Promise<void> {
    await this.$store.dispatch("federalstates/fetchStateCountyDataPellets", {
      stateNutsCode,
      countyNutsCode: countyNuts,
    });
  }

  async fetchPelletsStateChartData(nutsCode: string): Promise<void> {
    await this.$store.dispatch("federalstates/fetchPelletsStateChartData", {
      nutsCode,
    });
  }

  redirectToWithoutUmlauts(): void {
    const decodedId = decodePlaceName(this.districtName);
    if (this.districtName !== decodedId) {
      this.$nuxt.context.redirect(301, decodedId);
    }
  }

  filterAndShuffleCounties(): CountyLink[] {
    return this.countyLinks
      .filter(
        (county) =>
          decodePlaceName(county.link) !== decodePlaceName(this.districtName),
      )
      .sort(() => 0.5 - Math.random())
      .slice(0, 20);
  }

  handleError(error: Error): void {
    this.$nuxt.error({
      statusCode: 404,
      message: "Diese Ortschaft konnte nicht gefunden werden",
    });
  }

  get isDistrictCity(): boolean {
    return this.$route.params.id.startsWith("stadt-");
  }

  get areaTypeLabel(): string {
    return this.isDistrictCity ? "die kreisfreie Stadt" : "den Landkreis";
  }

  get districtName(): string {
    return this.$route.params.id.replace(/^stadt-/, "");
  }

  get moreThanTwentyCounties(): boolean {
    return this.countyLinks.length > 20;
  }

  get otherCountiesInBundesland(): CountyLink[] {
    return this.filterAndShuffleCounties();
  }

  get pelletsMetrics() {
    return calculatePelletsChartMetrics(this.pelletsChartData);
  }

  get returnPriceFormatDE() {
    return returnPriceFormatDE;
  }

  get isCityStateValue(): boolean {
    return isCityState(this.$route.params.category);
  }

  get pelletsAssetsPrice() {
    if (this.isCityStateValue) {
      return this.pelletsStateAssetsPrice;
    }
    return this.pelletsCountyAssetsPrice;
  }
}
